import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useSelector } from "react-redux";
import { ObjectId } from "bson";
// 1. Import the hook
import { useTranslation } from "react-i18next";

// --- LOGGER UTILITY ---
const Logger = {
  log: (category, message, data = null) => {
    const timestamp = new Date().toISOString();
    const logMessage = `[${timestamp}] [${category}] ${message}`;
    if (data) {
      console.log(logMessage, data);
    } else {
      console.log(logMessage);
    }
  },
  error: (category, message, error = null) => {
    const timestamp = new Date().toISOString();
    const logMessage = `[${timestamp}] [${category}] ❌ ERROR: ${message}`;
    if (error) {
      console.error(logMessage, error);
    } else {
      console.error(logMessage);
    }
  },
  warn: (category, message, data = null) => {
    const timestamp = new Date().toISOString();
    const logMessage = `[${timestamp}] [${category}] ⚠️ WARNING: ${message}`;
    if (data) {
      console.warn(logMessage, data);
    } else {
      console.warn(logMessage);
    }
  },
};

// --- VOICE ASSISTANT CONFIGURATION
const BASE_URL =
  process.env.REACT_APP_VOICE_TO_VOICE_URL || "ws://localhost:8000";
const WS_URL = `${BASE_URL.replace(/^http/, "ws")}/softskills-voice-chat`;

const REQUESTED_UPLINK_SAMPLE_RATE = 16000;
const DEFAULT_TTS_SAMPLE_RATE = 24000;
const PLAYBACK_BUFFER_TARGET_SECONDS = 0.2;

// --- IMPROVED: VOICE ACTIVITY DETECTION (VAD) CONFIGURATION ---
const IMPROVED_VAD_CONFIG = {
  VOICE_ACTIVITY_THRESHOLD: 0.03,
  SILENCE_DURATION_MS: 600,
  ENERGY_CONSISTENCY_WINDOW: 5,
  MIN_ENERGY_RATIO: 0.65,
  SPECTRAL_ROLLOFF_THRESHOLD: 0.88,
  ZCR_THRESHOLD: 0.3,
  SPIKE_THRESHOLD: 1.8,
  CONFIDENCE_THRESHOLD: 0.75,
};

const AITutorPage = () => {
  // 2. Initialize translation hook
  const { t, i18n } = useTranslation();
  
  const { user } = useSelector((state) => state.profile);
  Logger.log("INIT", "AITutorPage component initializing");

  // --- UI State ---
  const [isLoading, setIsLoading] = useState(true);
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);

  Logger.log("INIT", "User from Redux", user);

  // --- Context Selection State ---
  const [language, setLanguage] = useState("en-US");
  const className = useState(user?.class);

  // --- Voice Assistant State ---
  const [isRecording, setIsRecording] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState("disconnected");
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAIResponding, setIsAIResponding] = useState(false);

  const [email, setEmail] = useState("");
  const [characterName, setCharacterName] = useState("Tony Stark");

  const socketRef = useRef(null);
  const inputAudioContextRef = useRef(null);
  const audioCtxRef = useRef(null);
  const streamRef = useRef(null);

  const inputAudioWorkletNodeRef = useRef(null);
  const isPlayingRef = useRef(false);
  const currentAudioSourceRef = useRef(null);
  const playbackQueueRef = useRef([]);
  const nextStartTimeRef = useRef(0);
  const isAIRespondingRef = useRef(false);

  // --- VAD Refs ---
  const voiceActivityRef = useRef(false);
  const silenceTimeoutRef = useRef(null);
  const energyHistoryRef = useRef([]);
  const backgroundNoiseRef = useRef(0.001);
  const speechConfidenceRef = useRef(0);
  const endOfTurnTimerRef = useRef(null);

  const serverFormatRef = useRef({
    codec: "pcm_s16le",
    sampleRate: DEFAULT_TTS_SAMPLE_RATE,
    channels: 1,
  });

  const masterGainRef = useRef(null);
  const compressorRef = useRef(null);
  const filterRef = useRef(null);
  const bufferedDurationRef = useRef(0);

  // 3. Translate processing messages
  const processingMessages = [
    t("voicebot.gettingReady"),
    t("voicebot.loadingFunFacts"),
    t("voicebot.settingUpAdventure"),
    t("voicebot.tutorReady"),
  ];

  // 4. Sync initial language state with i18next on mount
  useEffect(() => {
    // If i18n is currently 'te', set local state to 'te-IN', etc.
    const currentLang = i18n.language;
    if (currentLang.startsWith('te')) setLanguage('te-IN');
    else if (currentLang.startsWith('hi')) setLanguage('hi-IN');
    else setLanguage('en-US');
  }, [i18n.language]);

  useEffect(() => {
    Logger.log("LOADING", "Starting loading animation");
    const interval = setInterval(() => {
      setLoadingMessageIndex((prevIndex) => {
        if (prevIndex === processingMessages.length - 1) {
          Logger.log("LOADING", "Loading complete, stopping animation");
          clearInterval(interval);
          setTimeout(() => {
            Logger.log("LOADING", "Transitioning to interactive view");
            setIsLoading(false);
          }, 500);
          return prevIndex;
        }
        Logger.log("LOADING", "Loading message index", prevIndex + 1);
        return prevIndex + 1;
      });
    }, 700);
    return () => {
      Logger.log("LOADING", "Cleaning up loading animation");
      clearInterval(interval);
    };
  }, [processingMessages.length]);

  // --- CORE AUDIO LOGIC ---

  const ensurePlaybackAudioContext = useCallback(() => {
    Logger.log("AUDIO_CONTEXT", "Ensuring playback audio context");
    if (!audioCtxRef.current || audioCtxRef.current.state === "closed") {
      try {
        Logger.log("AUDIO_CONTEXT", "Creating new AudioContext", {
          sampleRate: serverFormatRef.current.sampleRate,
        });
        audioCtxRef.current = new (window.AudioContext ||
          window.webkitAudioContext)({
          sampleRate: serverFormatRef.current.sampleRate,
          latencyHint: "interactive",
        });

        Logger.log(
          "AUDIO_CONTEXT",
          "Creating audio nodes (gain, compressor, filter)"
        );
        masterGainRef.current = audioCtxRef.current.createGain();
        compressorRef.current = audioCtxRef.current.createDynamicsCompressor();
        filterRef.current = audioCtxRef.current.createBiquadFilter();

        masterGainRef.current.gain.value = 0.9;
        filterRef.current.type = "highpass";
        filterRef.current.frequency.value = 120;
        compressorRef.current.threshold.value = -24;
        compressorRef.current.knee.value = 30;
        compressorRef.current.ratio.value = 3;
        compressorRef.current.attack.value = 0.003;
        compressorRef.current.release.value = 0.25;

        Logger.log("AUDIO_CONTEXT", "Connecting audio nodes");
        masterGainRef.current
          .connect(filterRef.current)
          .connect(compressorRef.current)
          .connect(audioCtxRef.current.destination);

        nextStartTimeRef.current = audioCtxRef.current.currentTime + 0.05;
        Logger.log("AUDIO_CONTEXT", "AudioContext created successfully", {
          state: audioCtxRef.current.state,
          nextStartTime: nextStartTimeRef.current,
        });
      } catch (e) {
        Logger.error("AUDIO_CONTEXT", "Failed to create AudioContext", e);
        setError("Web Audio API is not supported by this browser.");
      }
    }
    if (audioCtxRef.current && audioCtxRef.current.state === "suspended") {
      Logger.log("AUDIO_CONTEXT", "Resuming suspended AudioContext");
      audioCtxRef.current.resume();
    }
    return audioCtxRef.current;
  }, []);

  const stopAndClearPlayback = useCallback((reason = "unknown") => {
    Logger.log("PLAYBACK", `Stopping and clearing playback. Reason: ${reason}`);
    try {
      if (currentAudioSourceRef.current && audioCtxRef.current) {
        try {
          const now = audioCtxRef.current.currentTime;
          Logger.log("PLAYBACK", "Stopping current audio source", {
            currentTime: now,
          });
          currentAudioSourceRef.current.stop(now + 0.001);
          currentAudioSourceRef.current.disconnect();
        } catch (e) {
          Logger.warn("PLAYBACK", "Source already stopped", e.message);
        }
        currentAudioSourceRef.current = null;
      }

      if (masterGainRef.current && audioCtxRef.current) {
        try {
          const now = audioCtxRef.current.currentTime;
          Logger.log("PLAYBACK", "Fading out master gain");
          masterGainRef.current.gain.cancelScheduledValues(now);
          masterGainRef.current.gain.setValueAtTime(
            masterGainRef.current.gain.value,
            now
          );
          masterGainRef.current.gain.exponentialRampToValueAtTime(
            0.001,
            now + 0.01
          );
        } catch (e) {
          Logger.warn("PLAYBACK", "Gain fade error", e.message);
        }
      }

      if (audioCtxRef.current && audioCtxRef.current.state !== "closed") {
        Logger.log("PLAYBACK", "Closing AudioContext");
        audioCtxRef.current.close().catch(() => {});
      }
    } catch (e) {
      Logger.error("PLAYBACK", "Audio cleanup error", e);
    }

    Logger.log("PLAYBACK", "Resetting all playback state");
    audioCtxRef.current = null;
    masterGainRef.current = null;
    compressorRef.current = null;
    filterRef.current = null;
    playbackQueueRef.current = [];
    bufferedDurationRef.current = 0;
    isPlayingRef.current = false;
    isAIRespondingRef.current = false;
    nextStartTimeRef.current = 0;
    setIsAIResponding(false);
    Logger.log("PLAYBACK", "Playback cleanup complete");
  }, []);

  // --- VAD HELPER FUNCTIONS ---
  const calculateZCR = useCallback((audioData) => {
    let crossings = 0;
    for (let i = 1; i < audioData.length; i++) {
      if (audioData[i] >= 0 !== audioData[i - 1] >= 0) {
        crossings++;
      }
    }
    const zcr = crossings / audioData.length;
    Logger.log("VAD", "Zero Crossing Rate calculated", { zcr });
    return zcr;
  }, []);

  const calculateSpectralRolloff = useCallback((audioData) => {
    const highFreqEnergy = audioData
      .slice(audioData.length / 2)
      .reduce((sum, val) => sum + Math.abs(val), 0);
    const totalEnergy = audioData.reduce((sum, val) => sum + Math.abs(val), 0);
    const rolloff = totalEnergy > 0 ? highFreqEnergy / totalEnergy : 0;
    Logger.log("VAD", "Spectral Rolloff calculated", { rolloff });
    return rolloff;
  }, []);

  // --- REPLACED: VAD Function ---
  const improvedDetectVoiceActivity = useCallback(
    (audioData) => {
      const {
        VOICE_ACTIVITY_THRESHOLD,
        SILENCE_DURATION_MS,
        ENERGY_CONSISTENCY_WINDOW,
        MIN_ENERGY_RATIO,
        SPECTRAL_ROLLOFF_THRESHOLD,
        ZCR_THRESHOLD,
        SPIKE_THRESHOLD,
        CONFIDENCE_THRESHOLD,
      } = IMPROVED_VAD_CONFIG;

      Logger.log("VAD", "Detecting voice activity", {
        audioDataLength: audioData.length,
      });

      let sum = 0;
      let peak = 0;
      for (let i = 0; i < audioData.length; i++) {
        const val = audioData[i] * audioData[i];
        sum += val;
        if (val > peak) peak = val;
      }
      const rms = Math.sqrt(sum / audioData.length);
      Logger.log("VAD", "RMS and Peak calculated", {
        rms,
        peak: Math.sqrt(peak),
      });

      // Spike Detection
      const peakRatio = rms > 0 ? Math.sqrt(peak) / rms : 0;
      Logger.log("VAD", "Peak ratio", { peakRatio });
      if (peakRatio > SPIKE_THRESHOLD) {
        Logger.warn(
          "VAD",
          "Spike detected, likely transient sound (clap, click)",
          { peakRatio }
        );
        return;
      }

      // Dynamic background noise adaptation
      if (!voiceActivityRef.current) {
        backgroundNoiseRef.current =
          backgroundNoiseRef.current * 0.98 + rms * 0.02;
        Logger.log("VAD", "Background noise updated", {
          backgroundNoise: backgroundNoiseRef.current,
        });
      }

      const zcr = calculateZCR(audioData);
      const spectralRolloff = calculateSpectralRolloff(audioData);
      const dynamicThreshold = Math.max(
        VOICE_ACTIVITY_THRESHOLD,
        backgroundNoiseRef.current * 4
      );
      Logger.log("VAD", "Dynamic threshold calculated", { dynamicThreshold });

      // Criteria calculation
      const energyCriteria = rms > dynamicThreshold;
      const zcrCriteria = zcr < ZCR_THRESHOLD;
      const spectralCriteria = spectralRolloff < SPECTRAL_ROLLOFF_THRESHOLD;
      Logger.log("VAD", "Criteria evaluated", {
        energyCriteria,
        zcrCriteria,
        spectralCriteria,
      });

      energyHistoryRef.current.push(rms > dynamicThreshold ? 1 : 0);
      if (energyHistoryRef.current.length > ENERGY_CONSISTENCY_WINDOW) {
        energyHistoryRef.current.shift();
      }
      const consistencyRatio =
        energyHistoryRef.current.reduce((s, v) => s + v, 0) /
        energyHistoryRef.current.length;
      const consistencyCriteria = consistencyRatio >= MIN_ENERGY_RATIO;
      Logger.log("VAD", "Consistency evaluated", {
        consistencyRatio,
        consistencyCriteria,
      });

      // Confidence Score
      let confidence = 0;
      if (energyCriteria) confidence += 0.4;
      if (zcrCriteria) confidence += 0.25;
      if (spectralCriteria) confidence += 0.2;
      if (consistencyCriteria) confidence += 0.15;
      speechConfidenceRef.current = confidence;
      Logger.log("VAD", "Confidence score calculated", { confidence });

      const hasVoice = confidence >= CONFIDENCE_THRESHOLD;
      Logger.log("VAD", "Voice detection result", { hasVoice, confidence });

      if (hasVoice) {
        // --- BARGE-IN TRIGGER ---
        if (isAIRespondingRef.current && !voiceActivityRef.current) {
          Logger.log("BARGE_IN", "User interrupted AI response");
          stopAndClearPlayback("user_barge_in");
          if (socketRef.current?.readyState === WebSocket.OPEN) {
            Logger.log("WEBSOCKET", "Sending interrupt signal to server");
            socketRef.current.send(JSON.stringify({ type: "interrupt" }));
          }
        }
        // --- END OF BARGE-IN TRIGGER ---

        if (!voiceActivityRef.current) {
          Logger.log("VAD", "Voice activity started");
          voiceActivityRef.current = true;
          if (silenceTimeoutRef.current) {
            Logger.log("VAD", "Clearing silence timeout");
            clearTimeout(silenceTimeoutRef.current);
            silenceTimeoutRef.current = null;
          }
        }
      } else if (voiceActivityRef.current) {
        if (!silenceTimeoutRef.current) {
          Logger.log("VAD", "Starting silence timeout", {
            duration: SILENCE_DURATION_MS,
          });
          silenceTimeoutRef.current = setTimeout(() => {
            Logger.log("VAD", "Silence detected, voice activity ended");
            voiceActivityRef.current = false;
            energyHistoryRef.current = [];
            silenceTimeoutRef.current = null;
          }, SILENCE_DURATION_MS);
        }
      }
    },
    [calculateZCR, calculateSpectralRolloff, stopAndClearPlayback]
  );

  const int16ToFloat32 = useCallback((int16) => {
    Logger.log("AUDIO_CONVERSION", "Converting Int16 to Float32", {
      length: int16.length,
    });
    const float32 = new Float32Array(int16.length);
    for (let i = 0; i < int16.length; i++) {
      const s = int16[i];
      float32[i] = s < 0 ? s / 32768.0 : s / 32767.0;
    }
    Logger.log("AUDIO_CONVERSION", "Conversion complete");
    return float32;
  }, []);

  const processAndPlayQueue = useCallback(() => {
    Logger.log("PLAYBACK_QUEUE", "Processing and playing queue", {
      queueLength: playbackQueueRef.current.length,
      isPlaying: isPlayingRef.current,
    });

    const audioCtx = ensurePlaybackAudioContext();
    if (!audioCtx || playbackQueueRef.current.length === 0) {
      Logger.warn("PLAYBACK_QUEUE", "Cannot process queue", {
        hasAudioContext: !!audioCtx,
        queueLength: playbackQueueRef.current.length,
      });
      isPlayingRef.current = false;
      return;
    }

    const maxSamplesToProcess = audioCtx.sampleRate * 1.5;
    const chunksToProcess = [];
    let totalSamples = 0;

    for (const chunk of playbackQueueRef.current) {
      if (totalSamples + chunk.samples.length > maxSamplesToProcess) break;
      chunksToProcess.push(chunk);
      totalSamples += chunk.samples.length;
    }

    Logger.log("PLAYBACK_QUEUE", "Chunks to process", {
      chunkCount: chunksToProcess.length,
      totalSamples,
    });

    if (chunksToProcess.length === 0) {
      Logger.warn("PLAYBACK_QUEUE", "No chunks to process");
      return;
    }

    playbackQueueRef.current = playbackQueueRef.current.slice(
      chunksToProcess.length
    );
    Logger.log(
      "PLAYBACK_QUEUE",
      "Remaining queue length",
      playbackQueueRef.current.length
    );

    const consolidatedSamples = new Float32Array(totalSamples);
    let offset = 0;
    for (const chunk of chunksToProcess) {
      consolidatedSamples.set(chunk.samples, offset);
      offset += chunk.samples.length;
    }

    const sampleRate = chunksToProcess[0].sampleRate;
    bufferedDurationRef.current -= (totalSamples / sampleRate) * 1000;
    Logger.log("PLAYBACK_QUEUE", "Buffer duration updated", {
      bufferedDuration: bufferedDurationRef.current,
    });

    try {
      Logger.log("PLAYBACK_QUEUE", "Creating audio buffer", {
        length: consolidatedSamples.length,
        sampleRate,
      });
      const buffer = audioCtx.createBuffer(
        1,
        consolidatedSamples.length,
        sampleRate
      );
      buffer.copyToChannel(consolidatedSamples, 0, 0);

      const source = audioCtx.createBufferSource();
      source.buffer = buffer;
      source.connect(masterGainRef.current);
      currentAudioSourceRef.current = source;

      const now = audioCtx.currentTime;
      const startTime = Math.max(now, nextStartTimeRef.current);
      Logger.log("PLAYBACK_QUEUE", "Starting audio source", {
        now,
        startTime,
        duration: buffer.duration,
      });
      source.start(startTime);
      nextStartTimeRef.current = startTime + buffer.duration;

      if (!isAIRespondingRef.current) {
        Logger.log("PLAYBACK_QUEUE", "AI response started");
        isAIRespondingRef.current = true;
        setIsAIResponding(true);
      }

      source.onended = () => {
        Logger.log("PLAYBACK_QUEUE", "Audio source ended");
        if (currentAudioSourceRef.current === source) {
          currentAudioSourceRef.current = null;
        }
        isPlayingRef.current = false;
        if (playbackQueueRef.current.length > 0) {
          Logger.log(
            "PLAYBACK_QUEUE",
            "More chunks in queue, scheduling next playback"
          );
          setTimeout(() => processAndPlayQueue(), 5);
        } else {
          Logger.log(
            "PLAYBACK_QUEUE",
            "Queue empty, starting end-of-turn timer"
          );
          endOfTurnTimerRef.current = setTimeout(() => {
            Logger.log("PLAYBACK_QUEUE", "AI response ended");
            isAIRespondingRef.current = false;
            setIsAIResponding(false);
            endOfTurnTimerRef.current = null;
          }, 300);
        }
      };
    } catch (err) {
      Logger.error("PLAYBACK_QUEUE", "Error scheduling audio buffer", err);
      isPlayingRef.current = false;
      isAIRespondingRef.current = false;
      setIsAIResponding(false);
    }
  }, [ensurePlaybackAudioContext]);

  const handleIncomingAudio = useCallback(
    (arrayBuffer) => {
      Logger.log("AUDIO_INCOMING", "Received audio data", {
        byteLength: arrayBuffer.byteLength,
      });

      if (endOfTurnTimerRef.current) {
        Logger.log("AUDIO_INCOMING", "Clearing end-of-turn timer");
        clearTimeout(endOfTurnTimerRef.current);
        endOfTurnTimerRef.current = null;
      }

      if (arrayBuffer.byteLength % 2 !== 0) {
        Logger.warn("AUDIO_INCOMING", "Odd byte length, trimming last byte");
        arrayBuffer = arrayBuffer.slice(0, arrayBuffer.byteLength - 1);
      }

      const int16 = new Int16Array(arrayBuffer);
      const float32 = int16ToFloat32(int16);
      const sampleRate = serverFormatRef.current.sampleRate;

      Logger.log("AUDIO_INCOMING", "Adding to playback queue", {
        samples: float32.length,
        sampleRate,
      });
      playbackQueueRef.current.push({ samples: float32, sampleRate });

      bufferedDurationRef.current += (float32.length / sampleRate) * 1000;
      Logger.log("AUDIO_INCOMING", "Buffer duration updated", {
        bufferedDuration: bufferedDurationRef.current,
      });

      const targetBufferMs = PLAYBACK_BUFFER_TARGET_SECONDS * 1000;
      if (
        bufferedDurationRef.current >= targetBufferMs &&
        !isPlayingRef.current
      ) {
        Logger.log(
          "AUDIO_INCOMING",
          "Target buffer reached, starting playback",
          {
            bufferedDuration: bufferedDurationRef.current,
            targetBufferMs,
          }
        );
        isPlayingRef.current = true;
        processAndPlayQueue();
      }
    },
    [int16ToFloat32, processAndPlayQueue]
  );

  const connectWS = useCallback(
    (convId) => {
      Logger.log("WEBSOCKET", "Connecting to WebSocket", {
        conversationId: convId,
        url: WS_URL,
      });
      return new Promise((resolve, reject) => {
        if (socketRef.current?.readyState === WebSocket.OPEN) {
          Logger.log("WEBSOCKET", "Already connected");
          resolve();
          return;
        }
        setConnectionStatus("connecting");
        const ws = new WebSocket(WS_URL);
        socketRef.current = ws;
        ws.binaryType = "arraybuffer";

        ws.onopen = () => {
          Logger.log("WEBSOCKET", "Connection opened");
          setConnectionStatus("connected");

          const startMessage = {
            type: "start",
            config: {
              conversation_id: convId,
              language_code: language,
              character_name: characterName,
              email: email,
              mode: "softskills",
            },
          };

          Logger.log("WEBSOCKET", "Sending start message", startMessage);
          ws.send(JSON.stringify(startMessage));
          resolve();
        };

        ws.onmessage = (event) => {
          if (typeof event.data === "string") {
            try {
              const parsed = JSON.parse(event.data);
              Logger.log("WEBSOCKET", "Received message", parsed);

              if (parsed?.type === "clear") {
                Logger.log("WEBSOCKET", "Received clear command");
                stopAndClearPlayback("server_clear_command");
                if (ws.readyState === WebSocket.OPEN) {
                  Logger.log("WEBSOCKET", "Sending clear acknowledgment");
                  ws.send(JSON.stringify({ type: "clear_ack" }));
                }
                return;
              }
              if (parsed?.type === "format") {
                const newSampleRate =
                  parsed.sampleRate || DEFAULT_TTS_SAMPLE_RATE;
                Logger.log("WEBSOCKET", "Received format update", {
                  newSampleRate,
                });
                serverFormatRef.current = {
                  ...serverFormatRef.current,
                  ...parsed,
                  sampleRate: newSampleRate,
                };
              } else if (parsed?.type === "response_end") {
                Logger.log("WEBSOCKET", "Received response_end");
                isAIRespondingRef.current = false;
                setIsAIResponding(false);
              }
            } catch (e) {
              Logger.error("WEBSOCKET", "Failed to parse server JSON", e);
            }
          } else if (event.data instanceof ArrayBuffer) {
            Logger.log("WEBSOCKET", "Received binary audio data", {
              byteLength: event.data.byteLength,
            });
            if (audioCtxRef.current || ensurePlaybackAudioContext()) {
              handleIncomingAudio(event.data);
            }
          }
        };

        ws.onerror = (err) => {
          Logger.error("WEBSOCKET", "WebSocket error", err);
          setConnectionStatus("error");
          setError("WebSocket connection failed.");
          reject(err);
        };

        ws.onclose = () => {
          Logger.log("WEBSOCKET", "Connection closed");
          setConnectionStatus("disconnected");
          setIsRecording(false);
          stopAndClearPlayback("websocket_close");
        };
      });
    },
    [
      language,
      characterName,
      email,
      handleIncomingAudio,
      stopAndClearPlayback,
      ensurePlaybackAudioContext,
    ]
  );

  const startRecording = useCallback(async () => {
    Logger.log("RECORDING", "Starting recording");
    if (isRecording) {
      Logger.warn("RECORDING", "Already recording");
      return;
    }
    setError(null);
    stopAndClearPlayback("user_barge_in");

    try {
      const newConversationId = new ObjectId().toHexString();
      Logger.log("RECORDING", "New conversation ID", newConversationId);
      await connectWS(newConversationId);

      Logger.log("RECORDING", "Creating input AudioContext", {
        sampleRate: REQUESTED_UPLINK_SAMPLE_RATE,
      });
      const audioCtx =
        inputAudioContextRef.current ||
        new (window.AudioContext || window.webkitAudioContext)({
          sampleRate: REQUESTED_UPLINK_SAMPLE_RATE,
        });
      inputAudioContextRef.current = audioCtx;

      // --- UPDATED: Audio Worklet Code with High-Pass Filter ---
      const inputWorkletCode = `
        class AudioProcessor extends AudioWorkletProcessor {
          constructor(options) {
            super();
            this.bufferSize = 1024;
            this._buffer = new Float32Array(0);
            
            // High-pass filter coefficients for ~400Hz cutoff at 16kHz sample rate
            this.b0 = 0.9511;
            this.b1 = -1.9022;
            this.b2 = 0.9511;
            this.a1 = -1.9008;
            this.a2 = 0.9048;
            this.x1 = 0; this.x2 = 0; // input history
            this.y1 = 0; this.y2 = 0; // output history
          }
        
          applyFilter(inputSample) {
            const y = this.b0 * inputSample + this.b1 * this.x1 + this.b2 * this.x2 - this.a1 * this.y1 - this.a2 * this.y2;
            this.x2 = this.x1;
            this.x1 = inputSample;
            this.y2 = this.y1;
            this.y1 = y;
            return y;
          }

          process(inputs) {
            const channel = inputs[0]?.[0];
            if (!channel) return true;
            
            // Apply high-pass filter to the incoming audio
            const filteredChannel = new Float32Array(channel.length);
            for (let i = 0; i < channel.length; i++) {
                filteredChannel[i] = this.applyFilter(channel[i]);
            }
            
            this.port.postMessage({ type: 'voice_activity_data', audioData: Array.from(filteredChannel) });
            
            const newBuf = new Float32Array(this._buffer.length + filteredChannel.length);
            newBuf.set(this._buffer, 0);
            newBuf.set(filteredChannel, this._buffer.length);
            this._buffer = newBuf;
            
            while (this._buffer.length >= this.bufferSize) {
              const chunk = this._buffer.subarray(0, this.bufferSize);
              const int16 = new Int16Array(chunk.length);
              for (let i = 0; i < chunk.length; i++) {
                let s = Math.max(-1, Math.min(1, chunk[i]));
                int16[i] = s < 0 ? s * 32768 : s * 32767;
              }
              this.port.postMessage({ type: 'audio_data', buffer: int16.buffer }, [int16.buffer]);
              this._buffer = this._buffer.subarray(this.bufferSize);
            }
            return true;
          }
        }
        registerProcessor('audio-processor', AudioProcessor);
      `;

      Logger.log("RECORDING", "Adding audio worklet module");
      if (audioCtx.audioWorklet.addModule) {
        await audioCtx.audioWorklet.addModule(
          URL.createObjectURL(
            new Blob([inputWorkletCode], { type: "application/javascript" })
          )
        );
      }

      // --- UPDATED: Microphone Constraints ---
      Logger.log("RECORDING", "Requesting microphone access");
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: REQUESTED_UPLINK_SAMPLE_RATE,
          autoGainControl: false,
        },
      });
      Logger.log("RECORDING", "Microphone access granted");
      streamRef.current = stream;

      Logger.log("RECORDING", "Creating media stream source and worklet node");
      const source = audioCtx.createMediaStreamSource(stream);
      const workletNode = new AudioWorkletNode(audioCtx, "audio-processor");

      workletNode.port.onmessage = (event) => {
        if (event.data.type === "voice_activity_data") {
          improvedDetectVoiceActivity(event.data.audioData);
        } else if (event.data.type === "audio_data") {
          if (socketRef.current?.readyState === WebSocket.OPEN) {
            socketRef.current.send(event.data.buffer);
          }
        }
      };

      Logger.log("RECORDING", "Connecting audio nodes");
      source.connect(workletNode);
      inputAudioWorkletNodeRef.current = workletNode;
      setIsRecording(true);
      Logger.log("RECORDING", "Recording started successfully");

      if (audioCtx.state === "suspended") {
        Logger.log("RECORDING", "Resuming suspended audio context");
        await audioCtx.resume();
      }
    } catch (err) {
      Logger.error("RECORDING", "Failed to start recording", err);
      setError(
        err.message || "Could not start recording. Check mic permissions."
      );
      setIsRecording(false);
    }
  }, [
    isRecording,
    connectWS,
    improvedDetectVoiceActivity,
    stopAndClearPlayback,
  ]);

  const stopRecording = useCallback(() => {
    Logger.log("RECORDING", "Stopping recording");
    if (!isRecording) {
      Logger.warn("RECORDING", "Not currently recording");
      return;
    }
    setIsRecording(false);

    if (socketRef.current?.readyState === WebSocket.OPEN) {
      Logger.log("WEBSOCKET", "Sending stop message");
      socketRef.current.send(JSON.stringify({ type: "stop" }));
    }

    Logger.log("RECORDING", "Stopping media stream tracks");
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;

    Logger.log("RECORDING", "Disconnecting worklet node");
    inputAudioWorkletNodeRef.current?.disconnect();
    inputAudioWorkletNodeRef.current = null;

    if (
      inputAudioContextRef.current &&
      inputAudioContextRef.current.state !== "closed"
    ) {
      Logger.log("RECORDING", "Closing input audio context");
      inputAudioContextRef.current.close().then(() => {
        Logger.log("RECORDING", "Input audio context closed");
        inputAudioContextRef.current = null;
      });
    }

    if (silenceTimeoutRef.current) {
      Logger.log("RECORDING", "Clearing silence timeout");
      clearTimeout(silenceTimeoutRef.current);
      silenceTimeoutRef.current = null;
    }

    Logger.log("RECORDING", "Recording stopped successfully");
  }, [isRecording]);

  useEffect(() => {
    if (user) setEmail(user.email);
    Logger.log("CLEANUP", "Setting up cleanup effect");
    return () => {
      Logger.log("CLEANUP", "Running cleanup");
      if (isRecording) stopRecording();
      if (audioCtxRef.current && audioCtxRef.current.state !== "closed") {
        Logger.log("CLEANUP", "Closing audio context");
        audioCtxRef.current.close();
      }
    };
  }, [isRecording, stopRecording, user]);

  // --- UI Handlers ---
  const handleMicClick = () => {
    Logger.log("UI", "Mic button clicked", { isRecording });
    if (isRecording) {
      stopRecording();
    } else {
      setIsModalOpen(true);
    }
  };

  const handleSubmit = async (event) => {
    Logger.log("UI", "Form submitted");
    event.preventDefault();

    Logger.log("UI", "Starting session", {
      language,
      className,
    });
    setIsModalOpen(false);
    startRecording();
  };

  const viewVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.3, ease: "easeOut" },
    },
    exit: {
      opacity: 0,
      scale: 0.95,
      transition: { duration: 0.3, ease: "easeIn" },
    },
  };

  const renderLoadingView = () => {
    Logger.log("UI", "Rendering loading view");
    return (
      <motion.div
        key="loading-view"
        variants={viewVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        className="flex flex-col items-center"
      >
        <div className="mb-12 relative">
          <motion.div
            className="absolute inset-0 w-32 h-32 md:w-40 md:h-40 border-4 border-blue-400 rounded-full border-t-transparent mx-auto"
            animate={{ rotate: 360 }}
            transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
          />
          <div className="relative w-32 h-32 md:w-40 md:h-40 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center shadow-2xl">
            <span className="text-4xl md:text-5xl">🧠</span>
          </div>
        </div>
        <div className="mb-8 h-8 flex items-center justify-center">
          <AnimatePresence mode="wait">
            <motion.p
              key={loadingMessageIndex}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="text-xl md:text-2xl text-gray-400 font-medium"
            >
              {processingMessages[loadingMessageIndex]}
            </motion.p>
          </AnimatePresence>
        </div>
      </motion.div>
    );
  };

  const renderInteractiveView = () => {
    Logger.log("UI", "Rendering interactive view");
    return (
      <motion.div
        key="interactive-view"
        variants={viewVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        className="relative flex flex-col items-center justify-center h-full w-full p-4"
      >
        <div className="flex flex-col items-center justify-center flex-grow pt-10">
          <motion.div className="relative flex items-center justify-center">
            {(isAIResponding ||
              (!isRecording && connectionStatus === "disconnected")) && (
              <motion.div
                className="absolute bg-[#3B82F6] rounded-full"
                animate={{ scale: [1, 1.5, 1], opacity: [0.6, 0, 0.6] }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                style={{ width: "120px", height: "120px" }}
              />
            )}

            <motion.button
              className={`relative w-24 h-24 rounded-full flex items-center justify-center text-5xl cursor-pointer shadow-xl transition-colors duration-300 ${
                isRecording
                  ? "bg-red-600"
                  : "bg-gradient-to-br from-[#8B5CF6] to-[#3B82F6]"
              }`}
              whileHover={{ scale: 1.15 }}
              whileTap={{ scale: 0.9 }}
              disabled={connectionStatus === "connecting"}
              onClick={handleMicClick}
              animate={isRecording ? { scale: [1, 1.05, 1] } : {}}
              transition={
                isRecording
                  ? { duration: 1.5, repeat: Infinity, ease: "easeInOut" }
                  : {}
              }
            >
              {isRecording ? (
                <div className="flex items-center justify-center space-x-1.5">
                  {[...Array(5)].map((_, i) => (
                    <motion.div
                      key={i}
                      className="w-1.5 bg-white rounded-full shadow-lg"
                      animate={{
                        height: [16, 48, 16],
                        opacity: [0.6, 1, 0.6],
                      }}
                      transition={{
                        duration: 0.6,
                        repeat: Infinity,
                        ease: "easeInOut",
                        delay: i * 0.08,
                      }}
                    />
                  ))}
                </div>
              ) : (
                "🎙️"
              )}
            </motion.button>
          </motion.div>

          <div className="mt-10 text-xl text-slate-300 h-12 flex flex-col items-center justify-center">
            {error ? (
              <span className="text-red-400">{error}</span>
            ) : connectionStatus === "connecting" ? (
              // 5. Use translation
              t("voicebot.connecting")
            ) : isRecording ? (
              <div className="flex items-center space-x-2">
                {/* 5. Use translation */}
                <span>{t("voicebot.clickToStop")}</span>
              </div>
            ) : isAIResponding ? (
              // 5. Use translation
              t("voicebot.aiResponding")
            ) : (
              // 5. Use translation
              t("voicebot.clickToStart")
            )}
          </div>
        </div>

        <AnimatePresence>
          {isModalOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 backdrop-blur-sm flex items-center justify-center z-50"
              onClick={() => !isRecording && setIsModalOpen(false)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                transition={{ type: "spring", damping: 15 }}
                className="bg-[#20398a] w-full max-w-sm p-6 rounded-xl shadow-2xl border border-slate-700"
                onClick={(e) => e.stopPropagation()}
              >
                <form onSubmit={handleSubmit}>
                  <div className="space-y-4 pt-2">
                    <div>
                      <label
                        htmlFor="language-select"
                        className="block text-sm font-medium text-gray-300 mb-1 text-left"
                      >
                        {/* 5. Use translation */}
                        {t("voicebot.language")}
                      </label>
                      <select
                        id="language-select"
                        value={language}
                        onChange={(e) => {
                          const selectedLang = e.target.value;
                          Logger.log(
                            "UI",
                            "Language changed",
                            selectedLang
                          );
                          // 6. Update local state AND i18n
                          setLanguage(selectedLang);
                          const shortCode = selectedLang.split('-')[0]; // en, te, hi
                          i18n.changeLanguage(shortCode);
                        }}
                        disabled={isRecording}
                        className="w-full px-3 py-2 text-base text-gray-900 bg-white border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-200 disabled:cursor-not-allowed"
                      >
                        {/* 5. Use translation for options */}
                        <option value="en-US">{t("voicebot.english")}</option>
                        <option value="hi-IN">{t("voicebot.hindi")}</option>
                        <option value="te-IN">{t("voicebot.telugu")}</option>
                        <option value="ta-IN">{t("voicebot.tamil")}</option>
                        <option value="mr-IN">{t("voicebot.marathi")}</option>
                        <option value="kn-IN">{t("voicebot.kannada")}</option>
                      </select>
                    </div>
                    <div>
                      <label
                        htmlFor="character-name"
                        className="block text-sm font-medium text-gray-300 mb-1 text-left"
                      >
                         {/* 5. Use translation */}
                        {t("voicebot.characterName")}
                      </label>
                      <input
                        type="text"
                        id="character-name"
                        value={characterName}
                        onChange={(e) => setCharacterName(e.target.value)}
                        disabled={isRecording}
                        className="w-full px-3 py-2 text-base text-gray-700 bg-white border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-200 disabled:cursor-not-allowed"
                        placeholder="e.g. Tony Stark, Hermione Granger"
                      />
                    </div>
                  </div>
                  <button
                    type="submit"
                    className="mt-6 w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:bg-blue-800 disabled:cursor-not-allowed"
                    disabled={isRecording}
                  >
                     {/* 5. Use translation */}
                    {t("voicebot.startSession")}
                  </button>
                </form>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    );
  };

  Logger.log("UI", "Main render", { isLoading, isRecording, connectionStatus });

  return (
    <div className="flex h-screen w-full flex-col items-center justify-center bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 px-4 text-center text-white overflow-hidden">
      <div className="absolute inset-0 overflow-hidden">
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute bg-blue-400 rounded-full opacity-20"
            style={{
              width: `${Math.random() * 4 + 2}px`,
              height: `${Math.random() * 4 + 2}px`,
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -100, 0],
              x: [0, Math.random() * 100 - 50, 0],
              opacity: [0.2, 0.8, 0.2],
            }}
            transition={{
              duration: Math.random() * 10 + 10,
              repeat: Infinity,
              ease: "easeInOut",
              delay: Math.random() * 5,
            }}
          />
        ))}
      </div>
      <div className="relative z-10 flex flex-col items-center w-full max-w-2xl">
        <AnimatePresence mode="wait">
          {isLoading ? renderLoadingView() : renderInteractiveView()}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default AITutorPage;